import i18n from "../../../utils/i18n";
import { isEntrepreneurTaxCompanyType } from "../steps/ev/EvTaxForm.jsx";
import { evIndustryHasForeignTradeStep } from "../steps/ev/EvForeignTrade.jsx";
import { getIndustrySelectMeta } from "./industrySelectOptions";
import { industryHasActivityContent } from "./industryActivity";

const REVENUE_STEPS = [
  { data: 5, display: "0 - 5" },
  { data: 10, display: "5 - 12" },
  { data: 50, display: "12 - 50" },
  { data: 100, display: "50 - 100" },
  { data: 200, display: "100 - 200" },
  { data: 500, display: "200 - 500" },
  { data: 1999, display: "500 - 2000" },
  { data: 2000, display: "2000+" },
];

const REVENUE_UNKNOWN = -10;

const COMPANY_TYPE_VALUES = {
  kft: i18n.t("searchflow.companytypekft"),
  bt: i18n.t("searchflow.companytypebt"),
  zrt: i18n.t("searchflow.companytypezrt"),
  kkt: i18n.t("searchflow.companytypekkt"),
  nyrt: i18n.t("searchflow.companytypenyrt"),
  civil: i18n.t("providervafform.civil"),
  singleprimary: i18n.t("searchflow.companytypekata"),
  singlesecondary: i18n.t("searchflow.companytypeevsecondary"),
  kiva: i18n.t("searchflow.companytypeevprimary"),
  vszja: i18n.t("searchflow.companytypevszja"),
  entrepreneur: i18n.t("searchflow.companytypeentrepreneur"),
};

const ACTIVITY_GROUP_KEYS = {
  vehicle: "differentialvat",
  etrade: "etradesells",
  IT: "ittype",
  agriculture: "agriculturetype",
  art: "arttype",
  medicine: "medicinetype",
};

const ACTIVITY_FLAG_LABELS = {
  vehicle: {
    automotivepartstrading: "searchflow.automotivepartstrading",
    trading: "searchflow.vehicletrading",
    service: "searchflow.vehicleservice",
    other: "industrylabels.other",
  },
  etrade: {
    physicalproducts: "searchflow.physicalproducts",
    services: "searchflow.services",
    intellectualproperty: "searchflow.intellectualproperty",
    other: "industrylabels.other",
  },
  IT: {
    wagereview: "searchflow.wagereview",
    projectwork: "searchflow.projectwork",
    ownprodsoftdev: "searchflow.ownprodsoftdev",
    other: "industrylabels.other",
  },
  agriculture: {
    livestockfarming: "searchflow.livestockfarming",
    cropproduction: "searchflow.cropproduction",
    other: "industrylabels.other",
  },
  art: {
    createartworks: "searchflow.createartworks",
    performingartist: "searchflow.performingartist",
    tradeartworks: "searchflow.tradeartworks",
    other: "industrylabels.other",
  },
  medicine: {
    doctor: "searchflow.doctor",
    pharmacy: "searchflow.pharmacy",
    nurse: "searchflow.nurse",
    vet: "searchflow.vet",
    other: "industrylabels.other",
  },
};

const getRevenueLabel = (yearlyrevenue) => {
  if (yearlyrevenue === REVENUE_UNKNOWN) {
    return i18n.t("general.idk");
  }
  if (!yearlyrevenue || yearlyrevenue <= 0) {
    return null;
  }

  let nearest = REVENUE_STEPS[0];
  let smallestDistance = Infinity;
  REVENUE_STEPS.forEach((step) => {
    const distance = Math.abs(step.data - yearlyrevenue);
    if (distance < smallestDistance) {
      smallestDistance = distance;
      nearest = step;
    }
  });

  if (nearest.display === "2000+") {
    return "2000+ M Ft";
  }

  return `${nearest.display.replace(" - ", " — ")}M Ft`;
};

const getVatLabel = (vatFilter) => {
  if (vatFilter === "true") return i18n.t("general.yes");
  if (vatFilter === "false") return i18n.t("general.no");
  if (vatFilter === "idk") return i18n.t("general.idk");
  return null;
};

const getCompanyTypeLabel = (companytype) =>
  COMPANY_TYPE_VALUES[companytype] || null;

const getTaxFormLabel = (taxform, companytype) => {
  if (!taxform || isEntrepreneurTaxCompanyType(companytype)) {
    return null;
  }
  if (taxform === "kiva") return i18n.t("general.taxform1");
  if (taxform === "tao") return i18n.t("general.taxform2");
  if (taxform === "idk") return i18n.t("general.idk");
  return null;
};

const getEmployeeLabel = (noofemployees, companytype) => {
  if (isEntrepreneurTaxCompanyType(companytype)) {
    return null;
  }
  if (noofemployees === null || noofemployees === undefined || noofemployees === "") {
    return null;
  }
  const count = Number(noofemployees);
  if (Number.isNaN(count)) {
    return null;
  }
  return `${count} ${i18n.t("searchflow.employeecountsuffix")}`;
};

const getForeignTradeLabel = (industry, foreign_trade) => {
  if (!evIndustryHasForeignTradeStep(industry)) {
    return null;
  }
  if (foreign_trade === true) return i18n.t("general.yes");
  if (foreign_trade === false) return i18n.t("general.no");
  return null;
};

const getIndustryLabel = (industry, industryinput) => {
  if (!industry) return null;
  if (industry === "other") {
    const custom = String(industryinput || "").trim();
    return custom || i18n.t("industrylabels.other");
  }
  return getIndustrySelectMeta(industry)?.title || industry;
};

const getActivityLabel = (industry, industryspecifics) => {
  if (!industryHasActivityContent(industry)) {
    return null;
  }

  const slice = industryspecifics?.[industry];
  if (!slice) return null;

  if (industry === "beauty") {
    if (slice.beautyproductsales?.status === "yes") {
      return i18n.t("general.yes");
    }
    if (slice.beautyproductsales?.status === "no") {
      return i18n.t("general.no");
    }
    return null;
  }

  const flagMap = ACTIVITY_FLAG_LABELS[industry];
  const groupKey = ACTIVITY_GROUP_KEYS[industry];
  if (!flagMap || !groupKey) return null;

  const group = slice[groupKey];
  if (!group) return null;

  const activeKey = Object.keys(flagMap).find((key) => group[key]);
  if (!activeKey) return null;

  if (activeKey === "other") {
    const otherText = String(group.otherinput || "").trim();
    if (otherText) return otherText;
  }

  return i18n.t(flagMap[activeKey]);
};

/**
 * Summary chips for the contact step — reflects manual-path selections from company_store.
 * @param {object} companyStore
 * @param {string[]} [allowedKeys] - restricts chips to fields actually asked in the
 *   current flow (e.g. a flow that never asks about revenue shouldn't show a
 *   revenue chip just because company_store has an auto-filled default value).
 *   Omit to include every recognized field (the original full-searchFlow behavior).
 * @returns {{ category: string, value: string }[]}
 */
export const buildContactSelectionSummary = (companyStore, allowedKeys) => {
  const chips = [];
  const isAllowed = (key) => !allowedKeys || allowedKeys.includes(key);

  if (isAllowed("companytype")) {
    const companyType = getCompanyTypeLabel(companyStore.companytype);
    if (companyType) {
      chips.push({
        category: i18n.t("searchflow.categorycompanytype"),
        value: companyType,
      });
    }
  }

  if (isAllowed("taxform")) {
    const taxForm = getTaxFormLabel(companyStore.taxform, companyStore.companytype);
    if (taxForm) {
      chips.push({ category: i18n.t("general.taxformlabel"), value: taxForm });
    }
  }

  if (isAllowed("industry")) {
    const industry = getIndustryLabel(
      companyStore.industry,
      companyStore.industryinput,
    );
    if (industry) {
      chips.push({ category: i18n.t("general.industrylabel"), value: industry });
    }
  }

  if (isAllowed("activity")) {
    const activity = getActivityLabel(
      companyStore.industry,
      companyStore.industryspecifics,
    );
    if (activity) {
      chips.push({
        category: i18n.t("searchflow.categoryactivity"),
        value: activity,
      });
    }
  }

  if (isAllowed("employees")) {
    const employees = getEmployeeLabel(
      companyStore.noofemployees,
      companyStore.companytype,
    );
    if (employees) {
      chips.push({
        category: i18n.t("providervafform.noofemployees"),
        value: employees,
      });
    }
  }

  if (isAllowed("vat")) {
    const vat = getVatLabel(companyStore.vatFilter);
    if (vat) {
      chips.push({ category: i18n.t("searchflow.categoryvat"), value: vat });
    }
  }

  if (isAllowed("revenue")) {
    const revenue = getRevenueLabel(companyStore.yearlyrevenue);
    if (revenue) {
      chips.push({ category: i18n.t("general.revenuelabel"), value: revenue });
    }
  }

  if (isAllowed("foreigntrade")) {
    const foreignTrade = getForeignTradeLabel(
      companyStore.industry,
      companyStore.foreign_trade,
    );
    if (foreignTrade) {
      chips.push({
        category: i18n.t("searchflow.categoryforeigntrade"),
        value: foreignTrade,
      });
    }
  }

  return chips;
};
